import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectProps } from '@mui/material/Select'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import { useDebounce } from 'src/hooks/useDebounce'
import { Colors, FontWeight } from '../../constants/theme'

const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: 'none',
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
        border: `1px solid ${Colors.grey300}`,
        marginTop: 6,
        padding: 0
      }
    }
  }
}

const StyledSelect = (props: SelectProps) => {
  const { className, ...rest } = props
  return (
    <Select
      className={`text-[#30465F] bg-[#FFFFFF] rounded-full! h-[36px]! min-h-[36px]! border! border-solid! border-[#D1E9F4]! transition-all duration-200 ease-linear [&.MuiOutlinedInput-root]:rounded-full! [&.MuiOutlinedInput-root_.MuiOutlinedInput-notchedOutline]:border-none! [&_.MuiSelect-select]:py-[6px]! [&_.MuiSelect-select]:pr-[12px]! [&_.MuiSelect-select]:pl-[14px]! [&_.MuiSelect-select]:text-[13px]! [&_.MuiSelect-select]:font-medium [&_.MuiSelect-select]:min-h-0! [&_.MuiSelect-select]:h-auto! [&_.MuiSelect-select]:flex! [&_.MuiSelect-select]:items-center [&_.MuiSelect-select]:whitespace-nowrap [&_.MuiSelect-select]:overflow-hidden [&_.MuiSelect-select]:text-ellipsis [&_.MuiSelect-select]:leading-[1.2]! [&_.MuiSelect-icon]:text-[14px] [&_.MuiSelect-icon]:right-[10px] ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

interface CustomFilterMultiSelectProps {
  // Stable ID required to suppress MUI Select's internal useId() fallback
  // (which drifts between SSR and CSR when upstream tree shape differs - e.g.
  // dynamic() Suspense boundaries or auth-conditional renders in the nav
  // increment the useId counter at different rates server vs client). React
  // 19 strict attribute compare fires #418 on the native input's id attribute
  // (SSR `_R_il8n9nbcm6H1_` vs CSR `_r_4_`). Confirmed via at-error
  // captureHydrationDiff on /pension-estacionamiento/ciudad-de-mexico/coyoacan
  // (iOS Safari 26, UNINET CDMX, 2026-05-29). See CLAUDE.md "Trigger K".
  id: string
  value: string[]
  onChange: (value: string[]) => void
  options: Record<string, string>
  optionDescriptions?: Record<string, string>
  placeholder: string
  icon: string
  isActive: boolean
  size?: 'small' | 'medium'
  textSize?: 'small' | 'medium' | 'large'
  itemPadding?: 'compact' | 'comfortable' | 'spacious'
  footerLink?: { label: string; href: string }
  onShowAll?: { label: string; onClick: () => void }
}

const CustomFilterMultiSelect: React.FC<CustomFilterMultiSelectProps> = ({
  id,
  value,
  onChange,
  options,
  optionDescriptions,
  placeholder,
  icon,
  isActive,
  size = 'small',
  footerLink,
  onShowAll
}) => {
  const { t } = useTranslation('common')
  const [internalValue, setInternalValue] = useState<string[]>(value)
  const debouncedValue = useDebounce(internalValue, 1000)

  useEffect(() => {
    if (debouncedValue !== internalValue) return
    if (JSON.stringify(debouncedValue) !== JSON.stringify(value)) {
      onChange(debouncedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  const handleChange = (value: string | string[]) => {
    setInternalValue(Array.isArray(value) ? value : [value])
  }

  const getDisplayText = () => {
    if (internalValue.length === 0) {
      return placeholder
    }
    if (internalValue.length === 1) {
      return options[internalValue[0]]
    }
    return `${internalValue.length} ${t('FILTER.SIZES_SELECTED')}`
  }

  return (
    <FormControl size={size}>
      <StyledSelect
        // Explicit id/labelId/inputProps.id - suppresses MUI's internal
        // useId() for ALL three slots, eliminating the SSR/CSR id-attribute
        // drift (see id-prop comment above).
        id={id}
        labelId={`${id}-label`}
        inputProps={{ id: `${id}-input` }}
        multiple
        value={internalValue}
        onChange={(event) => handleChange(event.target.value as string[])}
        displayEmpty
        MenuProps={MenuProps}
        sx={{
          border: isActive
            ? `1px solid ${Colors.blue800}66 !important`
            : `1px solid ${Colors.grey400} !important`,
          backgroundColor: isActive
            ? `${Colors.blue500}10 !important`
            : `${Colors.white500} !important`,
          '&:hover': {
            borderColor: `${Colors.black500}66 !important`
          },
          '& .MuiSelect-select': {
            // blue800, not blue500: blue500 text is 2.7:1 on the tint.
            color: isActive ? Colors.blue800 : Colors.black700,
            fontWeight: isActive ? FontWeight.semiBold : FontWeight.medium
          },
          '& .MuiSelect-icon': {
            color: isActive ? Colors.blue800 : Colors.black400
          }
        }}
        renderValue={() => (
          <div className="flex w-full items-center gap-1.5 pr-3">
            <i
              className={`si ${icon} shrink-0 text-xs ${isActive ? 'text-blue-800' : 'text-ink-200'}`}
            />
            <span className="flex-1 truncate text-[13px]">
              {getDisplayText()}
            </span>
          </div>
        )}
      >
        {Object.entries(options).map(([key, label]) => {
          const isSelected = internalValue.includes(key)
          const description = optionDescriptions?.[key]

          return (
            <MenuItem
              key={key}
              value={key}
              sx={{
                padding: '12px 16px',
                margin: '0 8px',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: Colors.grey100
                },
                '&:first-of-type': {
                  marginTop: '8px'
                },
                '&.Mui-selected': {
                  backgroundColor: `${Colors.blue500}08 !important`
                },
                '&.Mui-selected:hover': {
                  backgroundColor: `${Colors.blue500}12 !important`
                }
              }}
            >
              <div className="flex w-full items-start gap-3">
                <div className="min-w-0 flex-1">
                  <span
                    className={`block text-sm font-semibold ${
                      isSelected ? 'text-blue-800' : 'text-ink-800'
                    }`}
                  >
                    {label}
                  </span>
                  {description && (
                    <span className="mt-0.5 block text-xs text-ink-200">
                      {description}
                    </span>
                  )}
                </div>
                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                    color: Colors.black400,
                    padding: 0,
                    marginTop: '1px',
                    flexShrink: 0,
                    '&.Mui-checked': {
                      color: Colors.blue800
                    },
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                />
              </div>
            </MenuItem>
          )
        })}

        {(onShowAll || footerLink) && (
          <li className="mx-4 mt-1 mb-3 border-t border-ink-100/15 pt-3">
            {onShowAll && (
              <button
                type="button"
                className="mb-2 flex w-full items-center gap-1.5 text-[13px] font-medium text-ink-200 transition-colors hover:text-ink-700"
                onClick={(e) => {
                  e.stopPropagation()
                  onShowAll.onClick()
                }}
              >
                <i className="si si-sliders text-xs" />
                {onShowAll.label}
              </button>
            )}
            {footerLink && (
              <a
                href={footerLink.href}
                className="flex items-center gap-1.5 text-[13px] font-semibold text-blue-800 underline decoration-blue-800/30 underline-offset-2 transition-colors hover:decoration-blue-800"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="si si-ruler-combined text-xs" />
                {footerLink.label}
              </a>
            )}
          </li>
        )}
      </StyledSelect>
    </FormControl>
  )
}

export default CustomFilterMultiSelect
